// @flow
import React from 'react';
import i18n from 'app/common/lib/i18n';
import ProductGroupsCarousel from 'app/common/react/components/profile/ProductGroups/ProductGroupsCarousel';
import ProductPinsCarousel from 'app/common/react/components/profile/ProductPins/ProductPinsCarousel';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import withRouteData from 'app/common/lib/react/withRouteData';
import { compose } from 'redux';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import { Box, Container } from 'gestalt';

const PRODUCT_GROUP_DATA_TYPE = 'productgroup';

type Props = {|
  onSeeAllButtonClick: () => {},
  overviewResource: Resource<Object>,
  user: ProfileUser,
  username: string,
  viewer: Viewer,
|};

function ProfileProductOverview({
  onSeeAllButtonClick,
  overviewResource,
  user,
  username,
  viewer,
}: Props) {
  if (
    !overviewResource ||
    !overviewResource.data ||
    !overviewResource.data[0].objects ||
    !user ||
    !user.has_catalog ||
    overviewResource.data[0].objects.length === 0
  ) {
    return null;
  }

  // Objects contains either pins or product groups based on APIs response
  const { objects } = overviewResource.data[0];
  const isProductGroup = objects.length > 0 && objects[0].type === PRODUCT_GROUP_DATA_TYPE;
  const isOwnProfile = viewer.isAuth && viewer.username === user.username;

  return (
    <Container>
      <SectionHeader
        label={i18n._('Featured Products', 'label for featured products on overview page')}
        link={`/${username}/_shop/`}
        onSeeAllButtonClick={onSeeAllButtonClick}
      />
      <Box justifyContent="center" display="flex">
        <Box maxWidth={785} width="100%">
          {isProductGroup ? (
            <ProductGroupsCarousel
              isOwnProfile={isOwnProfile}
              productGroups={objects}
              user={user}
            />
          ) : (
            <ProductPinsCarousel pins={objects} user={user} />
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default compose(
  withViewer,
  withResource({
    name: 'StoreFrontOverviewResource',
    key: 'overviewResource',
    options: ({ user, viewer }) => ({
      username: user.username,
      userId: user ? user.id : undefined,
      field_set_key: 'grid_item',
    }),
  }),
  withRouteData({
    name: 'ProfileProductOverview',
    routeContextLog: (context: Object, username) => {
      let userId;
      if ((context.user || {}).username === username) {
        userId = context.user.id;
      }
      return {
        object_id_str: userId,
        view_type: PCONST.thrift.ViewType.USER,
      };
    },
  }),
)(ProfileProductOverview);
